import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import {
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import ListAltIcon from '@material-ui/icons/ListAlt'

import { Container, SideDrawer } from '../../components'
import { useAppDispatch, useAptConfig, useOnScreen } from '../../hooks'
import {
    getUnitInspectionList,
    GET_UNIT_INSPECTION_LIST_REQUEST,
    RootState,
} from '../../store'
import {
    Damage,
    ModelListMap,
    ModelMap,
    UnitConfig,
    UnitInspection,
} from '../../models'
import { UnitInspectionCard } from './UnitInspectionCard'

interface Props {
    open: boolean
    inspectionId: number
    unitInspectionList: UnitInspection[]
    unitInspectionMap: ModelMap<UnitInspection>
    onClose: () => void
    onClickUnitInspection?: (unitInspection: UnitInspection) => void
    onClickDamage?: (unitInspection: UnitInspection) => void
    onClickUnitNotes?: (unitInspection: UnitInspection) => void
    onClickVideo?: (unitInspection: UnitInspection) => void
    selectedUnitInspectionId?: number
    searchText?: string
    onSearchChange?: (text: string) => void
    onApplySearch?: () => void
    unitDamageMap: ModelListMap<Damage>
}

export const UnitInspectionSideDrawer: React.FC<Props> = (props) => {
    const {
        open,
        inspectionId,
        unitInspectionList,
        onClose,
        onClickUnitInspection,
        onClickDamage,
        onClickUnitNotes,
        onClickVideo,
        selectedUnitInspectionId,
        searchText = '',
        onSearchChange,
        onApplySearch,
        unitDamageMap,
    } = props

    const theme = useTheme()
    const dispatch = useAppDispatch()

    const atBottomRef = useRef<HTMLDivElement>(null)

    const { getUnitConfigMap } = useAptConfig({ unitConfigList: true })
    const unitConfigMap = getUnitConfigMap()

    const nextLink = useSelector(
        (state: RootState) =>
            state.pagination.GET_UNIT_INSPECTION_LIST_REQUEST?.next,
    )

    const isLoading = useSelector(
        (state: RootState) =>
            state.inspection.isLoading[GET_UNIT_INSPECTION_LIST_REQUEST],
    )

    const atBottom = useOnScreen(atBottomRef)

    // Auto-fetch more data when scrolling to the bottom
    useEffect(() => {
        if (atBottom && nextLink && !isLoading) {
            dispatch(
                getUnitInspectionList({
                    url: nextLink,
                    inspection_id: inspectionId,
                }),
            )
        }
    }, [atBottom, nextLink, isLoading])

    return (
        <SideDrawer
            open={open}
            handleClose={onClose}
            width={400}
            title="Unit Inspections"
            alwaysOpen
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                {/* Search */}
                <div
                    style={{
                        padding: theme.spacing(2),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexShrink: 0,
                        display: 'flex',
                        gap: theme.spacing(1),
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search units..."
                        value={searchText}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                        style={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                onApplySearch?.()
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => onApplySearch?.()}
                        style={{ textTransform: 'none' }}
                    >
                        Apply
                    </Button>
                </div>

                {/* Unit Inspection Cards */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: theme.spacing(2),
                    }}
                >
                    {unitInspectionList.length === 0 && isLoading ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 200,
                            }}
                        >
                            <CircularProgress size={48} />
                        </div>
                    ) : unitInspectionList.length === 0 && !isLoading ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                color: theme.palette.text.secondary,
                                height: 200,
                            }}
                        >
                            <ListAltIcon
                                style={{ fontSize: 64, opacity: 0.3 }}
                            />
                            <span
                                style={{
                                    marginTop: theme.spacing(2),
                                    fontSize: '1rem',
                                }}
                            >
                                {searchText
                                    ? 'No matching unit inspections'
                                    : 'No unit inspections found'}
                            </span>
                        </div>
                    ) : (
                        <>
                            {unitInspectionList.map((unitInspection) => (
                                <UnitInspectionCard
                                    key={`UNIT_INSPECTION_CARD_${unitInspection.id}`}
                                    unitInspection={unitInspection}
                                    unitConfig={
                                        unitConfigMap[
                                            unitInspection.unit.unit_config
                                        ]
                                    }
                                    selected={
                                        selectedUnitInspectionId ===
                                        unitInspection.id
                                    }
                                    damageCount={
                                        unitDamageMap[unitInspection.unit.id]
                                            ?.length ?? 0
                                    }
                                    onClickCard={() =>
                                        onClickUnitInspection?.(unitInspection)
                                    }
                                    onClickDamage={() =>
                                        onClickDamage?.(unitInspection)
                                    }
                                    onClickUnitNotes={() =>
                                        onClickUnitNotes?.(unitInspection)
                                    }
                                    onClickVideo={() =>
                                        onClickVideo?.(unitInspection)
                                    }
                                />
                            ))}

                            {/* Bottom loader / infinite scroll trigger */}
                            <div
                                ref={atBottomRef}
                                style={{
                                    padding: theme.spacing(2),
                                    display: 'flex',
                                    justifyContent: 'center',
                                    minHeight: 50,
                                }}
                            >
                                {isLoading && <CircularProgress size={32} />}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </SideDrawer>
    )
}
